import { Box, Tooltip } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useIntersectionObserverRef } from "rooks";
import { CachePolicies } from "use-http";

import { BlockUI } from "components/common/BlockUI";
import { JANZU_URL } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { AssesmentsResponse } from "types/assesments";
import { removeEmptyValuesFromObject } from "utils/redirectDataPage";

import { DETECTORS_SIZE, LABELERS_SIZE, extractDateFromString } from "../utils";

import { AssesmentItem } from "./AssesmentItem";
import "./styles.css";

export interface Cell {
  yLabel: string;
  yId: string;
  yData: {
    xLabel: string;
    value: number | null;
    correct: number;
    total: number;
  }[];
}

interface HeatmapProps {
  data: AssesmentsResponse | undefined;
  filtersState: Record<string, string>;
  onClickCell: (data: any) => void;
}

export function Heatmap(props: HeatmapProps) {
  const { data, filtersState, onClickCell } = props;

  const [loading, setLoading] = useState<boolean>(false);
  const [listAssesments, setListAssesments] = useState<AssesmentsResponse | undefined>(data);

  const assesmentsUrl = `${JANZU_URL}reef-api/assessments`;
  const { get: getListAssesments } = useFetchCustom<AssesmentsResponse>(assesmentsUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const nextPage = useMemo(() => {
    if (listAssesments) {
      const labelersPage =
        listAssesments.labelers.length < listAssesments.labelers_total
          ? Math.ceil(listAssesments.labelers.length / LABELERS_SIZE) + 1
          : null;

      const detectorsPage =
        listAssesments.detectors.length < listAssesments.detectors_total
          ? Math.ceil(listAssesments.detectors.length / DETECTORS_SIZE) + 1
          : null;

      return {
        labelers: labelersPage,
        detectors: detectorsPage,
      };
    }
    return {
      labelers: null,
      detectors: null,
    };
  }, [listAssesments]);

  const xAxisLabels = useMemo(
    () => (listAssesments?.labelers ? listAssesments.labelers.map((labeler) => labeler.user_name) : []),
    [listAssesments]
  );
  const yAxisLabels = useMemo(() => (listAssesments?.detectors ? listAssesments.detectors : []), [listAssesments]);

  const gridCells: Cell[] = useMemo(
    () =>
      yAxisLabels.map((yData, yIndex) => ({
        yLabel: yData.label_prompt,
        yId: yData.id || yData.detector_id,
        yData: xAxisLabels.map((xLabel, xIndex) => {
          const assesmentData = listAssesments?.labelers
            ? listAssesments.labelers[xIndex].values[yIndex] || null
            : null;

          return {
            xLabel,
            value: assesmentData ? assesmentData.score : null,
            correct: assesmentData?.correct || 0,
            total: assesmentData?.total || 0,
          };
        }),
      })),
    [listAssesments, yAxisLabels, xAxisLabels]
  );

  // eslint-disable-next-line
  const fetchMoreAssesments = async (detectorsOffset: number | undefined, labelersOffset: number | undefined) => {
    setLoading(true);
    try {
      const urlObject = {
        ...filtersState,
        detectors_page: detectorsOffset || "",
        labelers_page: labelersOffset || "",
      };
      const queryString = new URLSearchParams(removeEmptyValuesFromObject(urlObject)).toString();
      const response = await getListAssesments(`?${queryString}`);
      if (response) {
        setListAssesments((prevData) => {
          const labelersData = prevData?.labelers
            ? prevData.labelers.map((labeler, idx) => ({
                ...labeler,
                values: [...labeler.values, ...response.labelers[idx].values],
              }))
            : [...response.labelers];
          const detectorsData = prevData?.detectors
            ? [...prevData.detectors, ...response.detectors]
            : [...response.detectors];
          return {
            ...response,
            labelers: labelersData,
            detectors: detectorsData,
          };
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreDetectors = useCallback(
    (entries) => {
      if (loading) return;

      if (entries && entries[0]) {
        if (entries[0].isIntersecting && nextPage.detectors) {
          fetchMoreAssesments(nextPage.detectors, undefined);
        }
      }
    },
    // eslint-disable-next-line
    [nextPage, fetchMoreAssesments, loading]
  );

  const fetchMoreLabelers = useCallback(
    (entries) => {
      if (loading) return;

      if (entries && entries[0]) {
        if (entries[0].isIntersecting && nextPage.labelers) {
          fetchMoreAssesments(undefined, nextPage.labelers);
        }
      }
    },
    // eslint-disable-next-line
    [nextPage, fetchMoreAssesments, loading]
  );

  const [loadMoreDetectorRef] = useIntersectionObserverRef(fetchMoreDetectors);
  const [loadMoreLabelerRef] = useIntersectionObserverRef(fetchMoreLabelers);

  return (
    <>
      <BlockUI active={loading}>
        <Box className="w-full overflow-x-auto py-2 px-4 rounded-md bg-white h-[70vh]">
          <Box className="heatmap horizontal" sx={{ gridTemplateRows: `repeat(${xAxisLabels.length + 1}, auto)` }}>
            <Box className="col" sx={{ gridTemplateColumns: `250px repeat(${xAxisLabels.length}, 80px)` }}>
              <Box className="w-full h-full bg-white flex justify-center items-center">
                <Typography fontWeight="semibold" align="center" className="text-[#0D297D]">
                  Detector List
                </Typography>
              </Box>
              {xAxisLabels.map((label, idx) => (
                <Box
                  key={label}
                  className="w-full h-full bg-[#FFFFFF] p-1 rounded-sm flex justify-center items-center cursor-pointer"
                  ref={idx === xAxisLabels.length - 1 ? loadMoreLabelerRef : null}
                >
                  <Tooltip title={label}>
                    <Typography variant="detail" align="center" noWrap>
                      {label}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Box>
            {gridCells.map((cell, idx) => (
              <AssesmentItem
                key={cell.yId}
                ref={idx === gridCells.length - 1 ? loadMoreDetectorRef : null}
                cellData={cell}
                onClickCell={onClickCell}
                xAxisLabelsLenght={xAxisLabels.length}
              />
            ))}
          </Box>
        </Box>
      </BlockUI>

      {data?.file_name && (
        <Typography className="mt-4 text-end w-full">
          Last Data Update: <span className="font-bold">{extractDateFromString(data?.file_name)}</span> (Data on this
          page is refreshed daily)
        </Typography>
      )}
    </>
  );
}
